import type { EvalCase, EvalSet } from '@agentisai/shared-types';
import { lines } from './format';

/**
 * The set editor's working copy. Free-text fields stay as typed (a tag list is
 * one comma-separated string, a regex list one-per-line) and are converted only
 * at save, so the user can type a half-finished value without it being
 * reformatted under the cursor. `original` carries every field the editor doesn't
 * touch (setup turns, state patches, `stages`, `source`…), so a save never
 * drops them.
 */
export interface DraftTurn {
  message: string;
  /** The saved turn this row came from (every field but `message`), or {} for a new row. */
  base: Record<string, unknown>;
}

export interface CaseDraft {
  original: EvalCase | null;
  id: string;
  name: string;
  tags: string;
  documents: string[];
  /** Each turn keeps its own original object, so removing one never shifts
   * per-turn fields (`forced_agents`…) onto a neighbour. */
  turns: DraftTurn[];
  matches: string;
  notMatches: string;
  judge: string;
  /** The judge score this case must reach, as typed ('' = the run's default). */
  passScore: string;
  /** True until the user edits the ID: the ID follows the name (see `withName`). */
  autoId: boolean;
}

export interface SetDraft {
  original: EvalSet | null;
  name: string;
  description: string;
  tags: string;
  cases: CaseDraft[];
}

const csv = (text: string) =>
  text
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

export function emptyCase(index: number): CaseDraft {
  return {
    original: null,
    id: `case-${String(index + 1).padStart(3, '0')}`,
    name: '',
    tags: '',
    documents: [],
    turns: [{ message: '', base: {} }],
    matches: '',
    notMatches: '',
    judge: '',
    passScore: '',
    autoId: true,
  };
}

export function toDraft(set: EvalSet | null): SetDraft {
  if (!set) return { original: null, name: '', description: '', tags: '', cases: [emptyCase(0)] };
  return {
    original: set,
    name: set.name,
    description: set.description ?? '',
    tags: (set.tags ?? []).join(', '),
    cases: set.cases.map((c) => ({
      original: c,
      id: c.id,
      name: c.name,
      tags: (c.tags ?? []).join(', '),
      documents: c.inputs.setup?.documents ?? [],
      turns: (c.inputs.turns ?? []).map(({ message, ...base }) => ({ message, base })),
      matches: (c.expect.reply_matches ?? []).join('\n'),
      notMatches: (c.expect.reply_not_matches ?? []).join('\n'),
      judge: c.expect.judge ?? '',
      passScore: typeof c.expect.pass_score === 'number' ? String(c.expect.pass_score) : '',
      autoId: false,
    })),
  };
}

/** Set `key` to `value`, or remove it when the value is empty — keeps saved sets minimal. */
function put<T extends object>(obj: T, key: string, value: unknown): T {
  const out = { ...obj } as Record<string, unknown>;
  const empty = value === '' || (Array.isArray(value) && value.length === 0);
  if (empty) delete out[key];
  else out[key] = value;
  return out as T;
}

export function fromDraft(draft: SetDraft): EvalSet {
  const cases: EvalCase[] = draft.cases.map((c) => {
    const orig = c.original;
    const setup = put({ ...(orig?.inputs.setup ?? {}) }, 'documents', c.documents);
    const turns = c.turns.map(({ message, base }) => ({ ...base, message }));
    let expect = { ...(orig?.expect ?? {}) };
    expect = put(expect, 'reply_matches', lines(c.matches));
    expect = put(expect, 'reply_not_matches', lines(c.notMatches));
    expect = put(expect, 'judge', c.judge.trim());
    const score = Number(c.passScore);
    expect = put(
      expect,
      'pass_score',
      c.passScore.trim() === '' || Number.isNaN(score) ? '' : score,
    );
    let out: EvalCase = {
      ...(orig ?? {}),
      id: c.id.trim(),
      name: c.name.trim(),
      // An e2e journey has no turns: dropping the empty list keeps it round-trip exact.
      inputs: put(
        put({ ...(orig?.inputs ?? {}) }, 'turns', turns),
        'setup',
        Object.keys(setup).length ? setup : '',
      ),
      expect,
    };
    out = put(out, 'tags', csv(c.tags));
    // A judge rubric is what makes a case "judge"-layer; keep the layer honest.
    if (!orig?.layer) out = put(out, 'layer', c.judge.trim() ? 'judge' : 'regex');
    return out;
  });
  let set: EvalSet = { ...(draft.original ?? {}), name: draft.name.trim(), cases };
  set = put(set, 'description', draft.description.trim());
  set = put(set, 'tags', csv(draft.tags));
  return set;
}

const EDITOR_EXPECT_KEYS = new Set(['judge', 'pass_score', 'reply_matches', 'reply_not_matches']);

/** A case ID from its name: `Glass excess, cited` → `glass-excess-cited`. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/** Rename a case; while its ID is still automatic, the ID follows the name. */
export function withName(c: CaseDraft, name: string, index: number): CaseDraft {
  if (!c.autoId) return { ...c, name };
  return { ...c, name, id: slugify(name) || `case-${String(index + 1).padStart(3, '0')}` };
}

/**
 * A copy placed after the original, same content, with an ID no other case uses
 * (`x-copy`, `x-copy-2`…) that still fits the 64-char ID rule. The ID is fixed
 * (not name-following), so renaming the copy never re-collides.
 */
export function duplicateCase(c: CaseDraft, taken: readonly string[]): CaseDraft {
  const base = c.id.slice(0, 55);
  let id = `${base}-copy`;
  for (let n = 2; taken.includes(id); n++) id = `${base}-copy-${n}`;
  return { ...c, name: c.name ? `${c.name} (copy)` : '', id, autoId: false };
}

/**
 * What stops each case from saving, in words a question-writer understands —
 * keyed by case index (-1 = the set itself). Schema issues are re-worded; on top
 * of the schema, a case must have SOMETHING to grade.
 */
export function draftIssues(
  draft: SetDraft,
  schemaIssues: { path: PropertyKey[]; message: string }[],
) {
  const out = new Map<number, string[]>();
  const add = (i: number, msg: string) => out.set(i, [...(out.get(i) ?? []), msg]);
  for (const issue of schemaIssues) {
    const [top, i, ...rest] = issue.path;
    if (top === 'cases' && typeof i === 'number') {
      const field = rest.includes('turns')
        ? 'a user message is empty or missing'
        : rest[0] === 'id'
          ? 'the ID can only use letters, numbers, - and _'
          : rest[0] === 'name'
            ? 'give it a name'
            : issue.message;
      add(i, field);
    } else if (top === 'name') {
      add(-1, 'the set name can only use lowercase letters, numbers and dashes');
    } else {
      add(-1, issue.message);
    }
  }
  draft.cases.forEach((c, i) => {
    // Typed checks the editor doesn't show (agents, stages, state…) count too,
    // and an e2e journey is graded by Playwright.
    const typed = Object.keys(c.original?.expect ?? {}).some((k) => !EDITOR_EXPECT_KEYS.has(k));
    const hasChecks =
      c.matches.trim() || c.notMatches.trim() || typed || c.original?.layer === 'e2e';
    if (!c.judge.trim() && !hasChecks) add(i, 'describe what a good answer does');
    if (c.passScore.trim() && !(Number(c.passScore) >= 0 && Number(c.passScore) <= 1)) {
      add(i, 'the pass score is a number from 0 to 1');
    }
    if (c.passScore.trim() && !c.judge.trim()) {
      add(i, 'a pass score only applies with a rubric — add one or clear the score');
    }
  });
  const ids = draft.cases.map((c) => c.id.trim());
  ids.forEach((id, i) => {
    if (ids.indexOf(id) !== i) add(i, `the ID "${id}" is already used by another case`);
  });
  return out;
}
